import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { apiRequest } from '../../../utils/api';
import { useEmpresaSelector } from '../../../context/EmpresaSelectorContext';
import './Admin.css';

const Empresas = () => {
  const { isAdmin, loading: authLoading, usuario } = useAuth();
  const { empresas, loading: empresasLoading, cargarEmpresas, empresaSeleccionada, cambiarEmpresa } = useEmpresaSelector();

  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [allUsuarios, setAllUsuarios] = useState([]);
  const [allCierresCaja, setAllCierresCaja] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    contacto: '',
    correoelectronico: '',
    ubicacion: '',
    rucCedula: ''
  });

  // Cargar datos de estadísticas (usuarios y cierres de caja)
  const cargarEstadisticas = useCallback(async () => {
    try {
      setStatsLoading(true);

      // Cargar todos los usuarios
      const usersResponse = await apiRequest('/users');
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setAllUsuarios(usersData);
      }

      // Cargar todos los cierres de caja (ingreso diario)
      const cierresResponse = await apiRequest('/cierres-caja');
      if (cierresResponse.ok) {
        const cierresData = await cierresResponse.json();
        setAllCierresCaja(cierresData);
      }
    } catch (err) {
      console.error('❌ Error cargando estadísticas:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Cargar empresas si es admin y no hay empresas cargadas
    if (usuario?.rol === 'conexadmin' && !empresasLoading && empresas.length === 0) {
      cargarEmpresas();
    }
  }, [usuario?.rol, empresasLoading, empresas.length, cargarEmpresas]);

  // Cargar estadísticas al montar
  useEffect(() => {
    if (usuario?.rol === 'conexadmin') {
      cargarEstadisticas();
    }
  }, [usuario?.rol, cargarEstadisticas]);

  // Calcular estadísticas por empresa
  const empresaStats = useMemo(() => {
    const stats = {};

    empresas.forEach(empresa => {
      const empresaId = String(empresa.id);

      // Contar administradores de esta empresa
      const admins = allUsuarios.filter(
        u => String(u.empresaId) === empresaId && u.rol === 'administrador'
      ).length;

      // Contar trabajadores de esta empresa
      const trabajadores = allUsuarios.filter(
        u => String(u.empresaId) === empresaId && u.rol === 'trabajadores'
      ).length;

      // Total de usuarios de esta empresa
      const totalUsuarios = allUsuarios.filter(
        u => String(u.empresaId) === empresaId
      ).length;

      // Calcular ingreso diario total de esta empresa
      const ingresoDiario = allCierresCaja
        .filter(c => String(c.empresaId) === empresaId)
        .reduce((sum, c) => sum + (parseFloat(c.pagado) || 0) + (parseFloat(c.abono) || 0), 0);

      stats[empresaId] = {
        admins,
        trabajadores,
        totalUsuarios,
        ingresoDiario
      };
    });

    return stats;
  }, [empresas, allUsuarios, allCierresCaja]);

  // Formatear moneda
  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-EC', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(valor || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingEmpresa
        ? `empresas/${editingEmpresa.id}`
        : 'empresas';

      const method = editingEmpresa ? 'PUT' : 'POST';

      const response = await apiRequest(url.startsWith('/') ? url : `/${url}`, {
        method,
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Error en la petición');
      }

      setShowForm(false);
      setEditingEmpresa(null);
      setFormData({
        nombre: '',
        descripcion: '',
        contacto: '',
        correoelectronico: '',
        ubicacion: '',
        rucCedula: ''
      });
      cargarEmpresas();
      cargarEstadisticas(); // Recargar estadísticas también
    } catch (err) {
      setError('Error al guardar empresa');
      console.error('Error saving empresa:', err);
    }
  };

  const handleEdit = (empresa) => {
    setEditingEmpresa(empresa);
    setFormData({
      nombre: empresa.nombre,
      descripcion: empresa.descripcion || '',
      contacto: empresa.contacto || '',
      correoelectronico: empresa.correoelectronico || '',
      ubicacion: empresa.ubicacion || '',
      rucCedula: empresa.rucCedula || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (empresaId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta empresa?')) {
      try {
        const response = await apiRequest(`/empresas/${empresaId}`, {
          method: 'DELETE'
        });

        if (!response.ok) {
          throw new Error('Error en la petición');
        }

        cargarEmpresas();
        cargarEstadisticas(); // Recargar estadísticas
      } catch (err) {
        setError('Error al eliminar empresa');
        console.error('Error deleting empresa:', err);
      }
    }
  };

  const handleSelectEmpresa = (empresa) => {
    cambiarEmpresa(empresa);
  };

  // Mostrar loading mientras se cargan los contextos
  if (authLoading || empresasLoading) {
    return (
      <div className="admin-container">
        <h2>Cargando...</h2>
      </div>
    );
  }

  // Verificar que el usuario esté disponible antes de verificar permisos
  if (!usuario) {
    return (
      <div className="admin-container">
        <h2>Cargando usuario...</h2>
      </div>
    );
  }

  // Verificar permisos después de que se haya cargado todo
  if (!isAdmin()) {
    return (
      <div className="admin-container">
        <h2>No tienes permisos para acceder a esta página</h2>
      </div>
    );
  }

  try {
    return (
      <div className="admin-container">
        <div className="admin-header">
          <h1>Administración de Empresas</h1>
          <button
            className="btn-primary"
            onClick={() => {
              setShowForm(true);
              setEditingEmpresa(null);
              setFormData({
                nombre: '',
                descripcion: '',
                contacto: '',
                correoelectronico: '',
                ubicacion: '',
                rucCedula: ''
              });
            }}
          >
            Nueva Empresa
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {showForm && (
          <div className="form-overlay">
            <div className="form-container">
              <h2>{editingEmpresa ? 'Editar Empresa' : 'Nueva Empresa'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nombre:</label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>RUC/Cédula:</label>
                    <input
                      type="text"
                      value={formData.rucCedula}
                      onChange={(e) => setFormData({ ...formData, rucCedula: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Contacto:</label>
                    <input
                      type="text"
                      value={formData.contacto}
                      onChange={(e) => setFormData({ ...formData, contacto: e.target.value })}
                      placeholder="Teléfono o nombre de contacto"
                    />
                  </div>
                  <div className="form-group">
                    <label>Correo Electrónico:</label>
                    <input
                      type="email"
                      value={formData.correoelectronico}
                      onChange={(e) => setFormData({ ...formData, correoelectronico: e.target.value })}
                      placeholder="correo@empresa.com"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Ubicación:</label>
                  <input
                    type="text"
                    value={formData.ubicacion}
                    onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })}
                    placeholder="Dirección de la empresa"
                  />
                </div>

                <div className="form-group">
                  <label>Descripción:</label>
                  <textarea
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    placeholder="Descripción de la empresa"
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    {editingEmpresa ? 'Actualizar' : 'Crear'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowForm(false);
                      setEditingEmpresa(null);
                      setFormData({
                        nombre: '',
                        descripcion: '',
                        contacto: '',
                        correoelectronico: '',
                        ubicacion: '',
                        rucCedula: ''
                      });
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {empresasLoading ? (
          <div className="loading">Cargando empresas...</div>
        ) : (
          <div className="empresas-grid">
            {empresas.map((empresa) => {
              const stats = empresaStats[String(empresa.id)] || { admins: 0, trabajadores: 0, totalUsuarios: 0, ingresoDiario: 0 };
              return (
                <div
                  key={empresa.id}
                  className={`empresa-card ${empresaSeleccionada?.id === empresa.id ? 'selected' : ''}`}
                >
                  <div className="empresa-header">
                    <h3>{empresa.nombre}</h3>
                    <div className="empresa-actions">
                      <button
                        className="btn-select"
                        onClick={() => handleSelectEmpresa(empresa)}
                      >
                        Seleccionar
                      </button>
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(empresa)}
                        title="Editar"
                      >
                        <span className="icon"><i className="fas fa-edit"></i></span>
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(empresa.id)}
                        title="Eliminar"
                      >
                        <span className="icon"><i className="fas fa-trash"></i></span>
                      </button>
                    </div>
                  </div>

                  <div className="empresa-info">
                    {empresa.rucCedula && (
                      <p className="empresa-detail">
                        <strong>RUC/Cédula:</strong> {empresa.rucCedula}
                      </p>
                    )}
                    {empresa.contacto && (
                      <p className="empresa-detail">
                        <strong>Contacto:</strong> {empresa.contacto}
                      </p>
                    )}
                    {empresa.correoelectronico && (
                      <p className="empresa-detail">
                        <strong>Email:</strong> {empresa.correoelectronico}
                      </p>
                    )}
                    {empresa.ubicacion && (
                      <p className="empresa-detail">
                        <strong>Ubicación:</strong> {empresa.ubicacion}
                      </p>
                    )}
                    {empresa.descripcion && (
                      <p className="empresa-description">{empresa.descripcion}</p>
                    )}
                  </div>

                  <div className="empresa-stats">
                    <div className="stat">
                      <span className="stat-label">Administradores:</span>
                      <span className="stat-value">
                        {statsLoading ? '...' : stats.admins}
                      </span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Trabajadores:</span>
                      <span className="stat-value">
                        {statsLoading ? '...' : stats.trabajadores}
                      </span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Ingreso Diario:</span>
                      <span className="stat-value">
                        {statsLoading ? '...' : formatearMoneda(stats.ingresoDiario)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  } catch (error) {
    console.error('❌ Error en renderizado de Empresas:', error);
    return (
      <div className="admin-container">
        <h2>Error al cargar la página</h2>
        <p>Error: {error.message}</p>
      </div>
    );
  }
};

export default Empresas; 
